import "./styles.scss";

import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";
import Parser from "@helpers/parser";
import { Text, MetricCard, Button, StatusChip, Icon } from "@fragments";
import { UserAvatar, CommunityBanner } from "@components/Community";
import {
  IconMail,
  IconPhone,
  IconFileText,
  IconCoin,
  IconCoinOff,
  IconShoppingCart,
  IconUserPlus,
  IconCheck,
  IconX,
  IconBan,
  IconAlertTriangle,
  IconUsersGroup,
  IconSchool,
  IconPercentage,
  IconClockHour4,
  IconFileDescription,
  IconTimeline,
  IconChartHistogram,
  IconSparkles,
  IconUserCheck,
  IconPlayerPlay,
  IconFlag,
  IconCircleCheck,
  IconCalendarTime,
  IconClipboardText,
  IconChevronRight,
} from "@tabler/icons-react";

import { getDurationUnitLabel } from "../../../enums/durationUnits";
import { getEnrollmentSourceLabel } from "../../../enums/enrollmentSources";
import { EnrollmentStatusChip } from "../../view";

// Chronological timeline config — each present timestamp becomes a step with its
// own icon. State (done / next / future) is derived against "now" at render.
// Built from the caller's `t` so the labels follow the active language.
const getTimelineSteps = (t) => [
  { key: "created_at", label: t("detail.timeline.steps.createdAt"), icon: IconSparkles },
  { key: "reviewed_at", label: t("detail.timeline.steps.reviewedAt"), icon: IconUserCheck },
  { key: "started_at", label: t("detail.timeline.steps.startedAt"), icon: IconPlayerPlay },
  { key: "ends_at", label: t("detail.timeline.steps.endsAt"), icon: IconFlag },
  { key: "split_ends_at", label: t("detail.timeline.steps.splitEndsAt"), icon: IconCoin },
  { key: "ended_at", label: t("detail.timeline.steps.endedAt"), icon: IconCircleCheck },
  { key: "split_ended_at", label: t("detail.timeline.steps.splitEndedAt"), icon: IconCoinOff },
];

export default function EnrollmentDetailView({
  enrollment,
  loading,
  notFound,
  onAction,
}) {
  const { t } = useTranslation("mentoringPrograms");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug, subtab: programId, itemId: enrollmentId } = useParams();
  const [openingCommunity, setOpeningCommunity] = useState(false);

  // Originating list tab (?tab=), preserved across the answers-page round-trip
  // so returning here keeps it for the "Voltar às inscrições" navigation.
  const tab = searchParams.get("tab");

  if (loading) {
    return (
      <div className="enrollment-detail px-4">
        <div className="ed-skeleton ed-skeleton--hero skel" />
        <div className="ed-metrics-row">
          <div className="ed-skeleton skel" style={{ height: 96 }} />
          <div className="ed-skeleton skel" style={{ height: 96 }} />
          <div className="ed-skeleton skel" style={{ height: 96 }} />
        </div>
        <div className="ed-grid">
          <div className="ed-skeleton skel" style={{ height: 240 }} />
          <div className="ed-skeleton skel" style={{ height: 240 }} />
        </div>
      </div>
    );
  }

  if (notFound || !enrollment) {
    return (
      <div className="enrollment-detail px-4">
        <div className="ed-empty">
          <Icon.Sphere size={72}>
            <IconUsersGroup />
          </Icon.Sphere>
          <Text.h4 contrast={6}>{t("detail.notFound")}</Text.h4>
        </div>
      </div>
    );
  }

  const { creator, community, program, timestamps = {}, insights } = enrollment;

  // The application is a form submission (enrollment.form_submission_id) against
  // the program's application form (program.application.form_id). Both are needed
  // to open the answers page; the card is hidden when either is missing.
  const applicationFormId = program?.application?.form_id;
  const applicationSubmissionId = enrollment.form_submission_id;
  const canViewApplication = Boolean(
    applicationSubmissionId && applicationFormId,
  );

  function openApplication() {
    if (!canViewApplication) return;
    // return_to brings the reviewer back here from the answers page; `mentoring`
    // carries the status so the answers page knows it's an approval/rejection.
    const returnTo = `/${slug}/settings/mentoring/${programId}/${enrollmentId}${tab ? `?tab=${tab}` : ""}`;
    const params = new URLSearchParams({
      form_id: applicationFormId,
      return_to: returnTo,
      mentoring: enrollment.status,
    });
    if (creator?.phone) params.set("phone", creator.phone);
    navigate(
      `/${slug}/settings/forms/submissions/${applicationSubmissionId}?${params.toString()}`,
    );
  }

  // The attached community's page lives at the app root (/:slug), so we need a
  // slug — the enrollment payload only carries the id. The mentor may not be a
  // member of it (so it won't be in the CommunityContext); look it up directly.
  async function openCommunity() {
    const id = community?.id;
    if (!id || openingCommunity) return;

    setOpeningCommunity(true);
    try {
      const res = await api.get(`community?id=${id}`);
      const targetSlug = res?.data?.slug;
      if (targetSlug) navigate(`/${targetSlug}`);
      else window.setSnackbar?.(t("detail.openCommunityError"));
    } finally {
      setOpeningCommunity(false);
    }
  }

  return (
    <div className="enrollment-detail px-4">
      {/* ── Hero: user info ──────────────────────────────────────────────── */}
      <div className="ed-hero">
        <span className="ed-hero-glow" aria-hidden="true" />
        <div className="ed-hero-main">
          {creator ? (
            <UserAvatar
              user={{ username: creator.name, avatar: creator.avatar_url }}
              size={80}
            />
          ) : (
            <Icon.Sphere size={80}>
              <IconMail />
            </Icon.Sphere>
          )}
          <div className="ed-hero-id">
            <Text.h2 weight={500}>
              {creator ? creator.name : enrollment.invited_email}
            </Text.h2>
            <div className="ed-hero-contact">
              {program && (
                <ContactItem
                  icon={<IconSchool size={16} />}
                  label={t("detail.contact.program")}
                  value={program.name}
                />
              )}
              {creator?.email && (
                <ContactItem
                  icon={<IconMail size={16} />}
                  label={t("detail.contact.email")}
                  value={creator.email}
                  copyable
                />
              )}
              {creator?.phone && (
                <ContactItem
                  icon={<IconPhone size={16} />}
                  label={t("common:fields.phone")}
                  value={Parser.phone(creator.phone)}
                  copyable
                />
              )}
            </div>
          </div>
        </div>

        <div className="ed-hero-side">
          <div className="ed-hero-meta">
            <EnrollmentStatusChip status={enrollment.status} />
            <StatusChip.Default
              text={
                getEnrollmentSourceLabel(t)[enrollment.source] ?? enrollment.source
              }
            />
          </div>
        </div>
      </div>

      {/* ── Metrics row (replaces the Insights card) ─────────────────────── */}
      {insights && (
        <div className="ed-insights">
          <div className="ed-section-head">
            <Text.h4 contrast={7} left={<IconChartHistogram size={15} />}>
              {t("detail.insights.title")}
            </Text.h4>
            <Text.span contrast={5} left={<IconCalendarTime size={14} />}>
              {fmtDate(insights.timestamps?.date_from)} –{" "}
              {fmtDate(insights.timestamps?.date_to)}
            </Text.span>
          </div>
          <div className="ed-metrics-row">
            <MetricCard
              title={t("detail.insights.revenue")}
              leftIcon={<IconCoin size={15} />}
              value={insights.total_revenue_cents / 100}
              variant="money"
            />
            <MetricCard
              title={t("detail.insights.orders")}
              leftIcon={<IconShoppingCart size={15} />}
              value={insights.total_orders}
              variant="number"
            />
            <MetricCard
              title={t("detail.insights.newMembers")}
              leftIcon={<IconUserPlus size={15} />}
              value={insights.new_members}
              variant="number"
            />
          </div>
        </div>
      )}

      {/* split_only callout */}
      {enrollment.status === "split_only" && (
        <div className="ed-callout ed-callout--info">
          <IconCoin size={18} />
          <Text.span contrast={8}>
            {t("detail.splitOnlyCallout", {
              date: fmtReadable(timestamps.split_ends_at),
            })}
          </Text.span>
        </div>
      )}

      {/* ── Programa & condições + Comunidade  |  Linha do tempo ─────────── */}
      <div
        className={`ed-grid${program || community ? "" : " ed-grid--single"}`}
      >
        {(program || community) && (
          <div className="ed-col">
            {program && (
              <Card
                title={t("detail.program.cardTitle")}
                icon={<IconFileDescription size={16} />}
              >
                {(canViewApplication ||
                  enrollment.status === "pending" ||
                  enrollment.status === "active") && (
                  <div className="ed-app-card">
                    {canViewApplication && (
                      <button
                        type="button"
                        className="ed-app-link"
                        onClick={openApplication}
                      >
                        <span className="ed-app-link-icon">
                          <IconClipboardText size={18} />
                        </span>
                        <span className="ed-app-link-text">
                          <Text.span contrast={8} weight={500}>
                            {t("detail.program.viewApplication")}
                          </Text.span>
                          <Text.span contrast={5}>
                            {t("detail.program.viewApplicationHint")}
                          </Text.span>
                        </span>
                        <IconChevronRight
                          size={16}
                          className="ed-app-link-chevron"
                        />
                      </button>
                    )}
                    <Actions status={enrollment.status} onAction={onAction} />
                  </div>
                )}

                <Row
                  label={t("detail.program.name")}
                  icon={<IconSchool size={14} />}
                  value={program.name}
                />
                <Row
                  label={t("detail.program.effectiveSplit")}
                  icon={<IconPercentage size={14} />}
                  value={`${enrollment.effective_split_percentage ?? program.split_percentage}%`}
                  emphasis
                />
                <Row
                  label={t("detail.program.duration")}
                  icon={<IconClockHour4 size={14} />}
                  value={
                    program.duration_value
                      ? `${program.duration_value} ${getDurationUnitLabel(t)[program.duration_unit]}`
                      : t("detail.program.durationFallback")
                  }
                />
                <Row
                  label={t("detail.program.splitWindow")}
                  icon={<IconCoin size={14} />}
                  value={
                    program.split_duration_value
                      ? `${program.split_duration_value} ${getDurationUnitLabel(t)[program.split_duration_unit]}`
                      : t("detail.program.splitWindowFallback")
                  }
                />
                {program.contract_url && (
                  <Row
                    label={t("detail.program.contract")}
                    icon={<IconFileText size={14} />}
                    value={
                      <a
                        className="ed-link"
                        href={program.contract_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Text.span
                          left={<IconFileText size={14} />}
                          contrast={7}
                        >
                          {t("detail.program.viewContract")}
                        </Text.span>
                      </a>
                    }
                  />
                )}
              </Card>
            )}

            {community && (
              <Card
                title={t("detail.community.cardTitle")}
                icon={<IconUsersGroup size={16} />}
              >
                <button
                  type="button"
                  className="ed-community"
                  onClick={openCommunity}
                  disabled={openingCommunity}
                  title={t("detail.community.openTitle", { name: community.name })}
                >
                  <CommunityBanner banner={community.banner_url} size={48} />
                  <div className="ed-community-info">
                    <Text.h4 contrast={8}>
                      {community.name}
                    </Text.h4>
                    <Text.span contrast={5}>
                      {t("detail.community.membersCount", { value: community.members_count })}
                    </Text.span>
                  </div>
                  <IconChevronRight size={16} className="ed-community-chevron" />
                </button>
              </Card>
            )}
          </div>
        )}

        <div className="ed-col">
          <Card title={t("detail.timeline.cardTitle")} icon={<IconTimeline size={16} />}>
            {enrollment.premature_end && (
              <div className="ed-badge ed-badge--warning">
                <IconAlertTriangle size={14} />
                <Text.span contrast={8}>{t("detail.timeline.prematureEnd")}</Text.span>
              </div>
            )}
            <Timeline timestamps={timestamps} />
            {enrollment.notes && (
              <div className="ed-notes">
                <Text.span contrast={5}>{t("detail.timeline.notes")}</Text.span>
                <Text.p contrast={7}>{enrollment.notes}</Text.p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Actions({ status, onAction }) {
  const { t } = useTranslation("mentoringPrograms");

  if (status === "pending") {
    return (
      <div className="ed-app-actions">
        <Button
          text={t("detail.actions.accept")}
          startIcon={<IconCheck size={16} />}
          discreet
          color="success"
          size="small"
          onClick={() => onAction?.("accept")}
          preventLoading
          fullWidth
        />
        <Button
          text={t("detail.actions.reject")}
          startIcon={<IconX size={16} />}
          discreet
          color="error"
          size="small"
          onClick={() => onAction?.("reject")}
          preventLoading
          fullWidth
        />
      </div>
    );
  }
  if (status === "active") {
    return (
      <div className="ed-app-actions">
        <Button
          text={t("detail.actions.end")}
          startIcon={<IconBan size={16} />}
          color="error"
          size="small"
          onClick={() => onAction?.("end")}
          preventLoading
          fullWidth
        />
      </div>
    );
  }
  return null;
}

// Hero contact item — circular icon + (label / value) stack. `copyable` makes
// it a button that copies the value; otherwise it's a plain display row.
function ContactItem({ icon, label, value, copyable }) {
  const { t } = useTranslation("mentoringPrograms");
  const Tag = copyable ? "button" : "div";
  function copy() {
    if (!copyable) return;
    navigator.clipboard
      ?.writeText(value)
      .then(() => window.setSnackbar?.(t("common:actions.copied")));
  }
  return (
    <Tag
      type={copyable ? "button" : undefined}
      className={`ed-contact-item${copyable ? " is-copyable" : ""}`}
      onClick={copy}
      title={copyable ? t("detail.contact.copyTitle", { value }) : undefined}
    >
      <span className="ed-contact-icon">{icon}</span>
      <span className="ed-contact-body">
        <Text.span contrast={5}>{label}</Text.span>
        <Text.span contrast={8} weight={500}>
          {value}
        </Text.span>
      </span>
    </Tag>
  );
}

function Timeline({ timestamps }) {
  const { t } = useTranslation("mentoringPrograms");
  const events = buildTimeline(t, timestamps);

  if (!events.length) {
    return <Text.span contrast={4}>{t("detail.timeline.empty")}</Text.span>;
  }

  return (
    <div className="ed-timeline">
      {events.map((event, i) => {
        const StepIcon = event.icon;
        return (
          <div
            className={`ed-tl-item is-${event.state}`}
            key={event.key}
            style={{ "--i": i }}
          >
            <span className="ed-tl-dot">
              <StepIcon size={14} />
            </span>
            <div className="ed-tl-content">
              <Text.span contrast={8}>{event.label}</Text.span>
              <Text.span contrast={5}>{fmtReadable(event.value)}</Text.span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Maps present timestamps to chronological steps and tags each: "done" (in the
// past), "next" (the first upcoming one) or "future".
function buildTimeline(t, timestamps) {
  const now = Date.now();
  let nextTaken = false;

  return getTimelineSteps(t).map((step) => ({
    ...step,
    value: timestamps[step.key],
  }))
    .filter((step) => step.value)
    .map((step) => {
      const done = new Date(step.value).getTime() <= now;
      let state = "future";
      if (done) {
        state = "done";
      } else if (!nextTaken) {
        state = "next";
        nextTaken = true;
      }
      return { ...step, state };
    });
}

function Card({ title, icon, children }) {
  return (
    <div className="ed-card">
      <div className="ed-card-head">
        {icon && <span className="ed-card-icon">{icon}</span>}
        <Text.h5 contrast={8} weight={500}>
          {title}
        </Text.h5>
      </div>
      <div className="ed-card-body">{children}</div>
    </div>
  );
}

function Row({ label, value, icon, emphasis }) {
  return (
    <div className="ed-row">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      {typeof value === "string" ? (
        <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
          {value}
        </Text.span>
      ) : (
        value
      )}
    </div>
  );
}

function fmtReadable(iso) {
  return iso ? Parser.date.readable(iso) : "—";
}

function fmtDate(iso) {
  return iso ? Parser.date.ddmmyyyy(iso.slice(0, 10)) : "—";
}
